// Implementing a pub/sub pattern with a central state manager
class TDFilterStateManager {
  constructor() {
    if (TDFilterStateManager.instance) {
      return TDFilterStateManager.instance;
    }
    
    TDFilterStateManager.instance = this;
    
    // Initialize state
    this.state = {
      sortOption: '',
      comparisonSet: [],
      filterSet: []
    };
    
    // Initialize subscribers
    this.subscribers = [];
  }
  
  // Subscribe to state changes
  subscribe(callback) {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter(sub => sub !== callback);
    };
  }
  
  // Notify all subscribers of state change
  notifySubscribers(prevState) {
    this.subscribers.forEach(callback => callback(this.state, prevState));
  }
  
  // Update sort option
  setSortOption(sortOption) {
    const prevState = {...this.state};
    this.state.sortOption = sortOption;
    this.notifySubscribers(prevState);
    return this.state;
  }
  
  // Update comparison set
  setComparisonSet(comparisonSet) {
    const prevState = {...this.state};
    this.state.comparisonSet = comparisonSet;
    this.notifySubscribers(prevState);
    return this.state;
  }
  
  // Update filter set
  setFilterSet(filterSet) {
    const prevState = {...this.state};
    this.state.filterSet = filterSet;
    this.notifySubscribers(prevState);
    return this.state;
  }
  
  // Clear all filters and state
  clearAll() {
    const prevState = {...this.state};
    this.state = {
      sortOption: '',
      comparisonSet: [],
      filterSet: []
    };
    this.notifySubscribers(prevState);
    
    // Dispatch a single clear-all event for backward compatibility
    document.dispatchEvent(new CustomEvent('td:clear-all', {
      bubbles: true,
      detail: {
        collections: [],
        filters: [],
        sort: ''
      }
    }));
    
    return this.state;
  }
  
  // Get current state
  getState() {
    return {...this.state};
  }
}

const stateManagerInstance = new TDFilterStateManager();
const getStateManager = () => stateManagerInstance;

if (!customElements.get('td-list-collection')) {
  class TDListCollection extends HTMLElement {
    constructor() {
      super()
      this.OPTION_SELECTOR = '.td--collection-card__wrapper'
      this.CONTAINER_SELECTOR = '#Collection-'
      this.SLIDEOUT_TRIGGER_SELECTOR = '.td--filter-sort__trigger'
      this.REPLACEMENT_SELECTOR = '[data-replacement]'
      this.LOADING_CLASS = 'td--loading'
      this.LOADING_STATE_ATTR = 'data-loading'

      if (!this.dataset.sectionId || !this.dataset.collectionHandle) {
        console.error('TD - List Collection is missing required attributes:', {
          'data-section-id': this.dataset.sectionId ? '✓' : '✗',
          'data-collection-handle': this.dataset.collectionHandle ? '✓' : '✗'
        });
        return;
      }

      // Use state manager instead of local state
      this.stateManager = getStateManager();
      this.visibleOptions = []

      this.container = undefined 
      this.options = undefined
      this.slideoutTrigger = undefined
      this.replacementContainer = undefined
    }
    connectedCallback() {
      // Initialize once DOM is fully loaded
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initialize())
      } else {
        this.initialize()
      }
    }

    initialize() {
      // Initialize elements
      this.options = this.querySelectorAll(this.OPTION_SELECTOR)
      this.container = this.querySelector(this.CONTAINER_SELECTOR + this.dataset.sectionId)
      this.slideoutTrigger = this.querySelector(this.SLIDEOUT_TRIGGER_SELECTOR)
      this.replacementContainer = this.querySelector(this.REPLACEMENT_SELECTOR)

      if (!this.container) {
        console.error(`TD - Collection container #Collection-${this.dataset.sectionId} not found`)
        return
      }
      if (!this.options.length) {
        console.error('TD - No collection cards found')
        return
      }

      // Subscribe to state changes
      this.unsubscribe = this.stateManager.subscribe((newState, prevState) => {
        // Only update UI if relevant state has changed
        if (
          prevState.sortOption !== newState.sortOption ||
          prevState.comparisonSet.length !== newState.comparisonSet.length ||
          prevState.filterSet.length !== newState.filterSet.length ||
          JSON.stringify(prevState.comparisonSet) !== JSON.stringify(newState.comparisonSet) ||
          JSON.stringify(prevState.filterSet) !== JSON.stringify(newState.filterSet)
        ) {
          this.handleUIUpdates();
        }
      });

      // For backward compatibility, still listen to these events
      this._boundListeners = {
        handleComparisonSet: this.handleComparisonSet.bind(this),
        handleSortSet: this.handleSortSet.bind(this),
        handleFilterSet: this.handleFilterSet.bind(this)
      }

      // Add event listeners for backward compatibility
      document.addEventListener('td:compare-set', this._boundListeners.handleComparisonSet)
      document.addEventListener('td:sort-set', this._boundListeners.handleSortSet)
      document.addEventListener('td:filter-set', this._boundListeners.handleFilterSet)

      // Store the slideout handler function
      if (this.slideoutTrigger) {
        this._handleSlideoutOpen = () => document.dispatchEvent(new CustomEvent("td:slideout-open"));
        this.slideoutTrigger.addEventListener('click', this._handleSlideoutOpen);
      }
      if (this.replacementContainer) {
        this.handleTitleReplacement()
      }
    }

    handleComparisonSet({detail}) {
      // Update state through state manager
      this.stateManager.setComparisonSet(detail.collections);
      
      // For backward compatibility
      document.dispatchEvent(new CustomEvent("td:update-compare-set", {
        detail: { options: detail.collections }
      }));
    }

    handleSortSet({detail}) {
      // Update state through state manager
      this.stateManager.setSortOption(detail.sort);
      
      // For backward compatibility
      document.dispatchEvent(new CustomEvent("td:update-sort-set", {
        detail: { value: detail.sort }
      }));
    }

    handleFilterSet({detail}) {
      // Update state through state manager
      this.stateManager.setFilterSet(detail.type);
      
      // For backward compatibility
      document.dispatchEvent(new CustomEvent("td:update-filter-set", {
        detail: detail.type
      }));
    }
    handleTitleReplacement() {
      const values = this.replacementContainer.dataset.values.split(',')
      
      // Store handlers for cleanup
      this._titleReplacementInterval = setInterval(() => {
        const handleTransitionEnd = () => {
          const cIndex = parseInt(this.replacementContainer.dataset.item) + 1
          if (cIndex > values.length - 1) {
            this.replacementContainer.innerHTML = values[0]
            this.replacementContainer.setAttribute('data-item', 0)
          } else {
            this.replacementContainer.innerHTML = values[cIndex]
            this.replacementContainer.setAttribute('data-item', cIndex)
          }
          this.replacementContainer.classList.remove('td--transitioning')
        }

        this.replacementContainer.addEventListener('transitionend', handleTransitionEnd, { once: true })
        this.replacementContainer.classList.add('td--transitioning')
      }, this.dataset.replacementTime || 1000)
    }

    disconnectedCallback() {
      // Unsubscribe from state changes
      if (this.unsubscribe) {
        this.unsubscribe();
      }
      
      // Clean up all document event listeners
      if (this._boundListeners) {
        document.removeEventListener('td:compare-set', this._boundListeners.handleComparisonSet)
        document.removeEventListener('td:sort-set', this._boundListeners.handleSortSet)
        document.removeEventListener('td:filter-set', this._boundListeners.handleFilterSet)
      }

      // Clean up slideout trigger event handler
      if (this.slideoutTrigger && this._handleSlideoutOpen) {
        this.slideoutTrigger.removeEventListener('click', this._handleSlideoutOpen)
      }

      // Clear title replacement interval
      if (this._titleReplacementInterval) {
        clearInterval(this._titleReplacementInterval)
      }

      // Clear stored handlers
      this._boundListeners = null
      this._handleSlideoutOpen = null
      this._titleReplacementInterval = null
    }
    async fetchSectionHTML() {
      const url = window.Shopify.routes.root + 'collections/' + this.dataset.collectionHandle;
      const params = new URLSearchParams({
        section_id: this.dataset.sectionId
      });

      // Get current state from state manager
      const { sortOption, filterSet, comparisonSet } = this.stateManager.getState();

      // Handle sort separately since it's not a filter
      if (sortOption) {
        params.append('sort_by', sortOption);
      }

      // Add all filters
      if (filterSet.length > 0) {
        filterSet.forEach(type => {
          params.append('filter.p.product_type', type);
        });
      }

      if (comparisonSet.length > 0) {
        comparisonSet.forEach(tag => {
          params.append('filter.p.tag', tag);
        });
      }
      
      const finalUrl = `${url}?${params.toString()}`;
      
      try {
        const response = await fetch(finalUrl);
        if (!response.ok) {
          const errorText = await response.text();
          console.error('TD - Section fetch response:', {
            status: response.status,
            statusText: response.statusText,
            response: errorText.substring(0, 500) // First 500 chars only
          });
          throw new Error(`Section fetch failed with status ${response.status}: ${response.statusText}`);
        }
        const html = await response.text();
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const container = doc.querySelector(this.CONTAINER_SELECTOR + this.dataset.sectionId);
        if (!container) {
          console.error('TD - Section response parsing failed:', {
            selector: this.CONTAINER_SELECTOR + this.dataset.sectionId,
            html: html.substring(0, 200) + '...' // First 200 chars for context
          });
          throw new Error(`Container #Collection-${this.dataset.sectionId} not found in section response`);
        }
        return container.innerHTML;
      } catch (error) {
        console.error('Failed to fetch section:', {
          url: `${url}?${params.toString()}`,
          error: error.message,
          sectionId: this.dataset.sectionId
        });
        return null;
      }
    }

    async handleUIUpdates() {
      this.setAttribute(this.LOADING_STATE_ATTR, 'true');
      this.container.classList.add(this.LOADING_CLASS);
      
      const newHTML = await this.fetchSectionHTML();
      
      this.container.classList.remove(this.LOADING_CLASS);
      this.setAttribute(this.LOADING_STATE_ATTR, 'false');
      
      if (newHTML) {
        this.container.innerHTML = newHTML;
      } else {
        console.warn('TD - List Collection section update failed - keeping existing content');
      }

      // Always re-query and initialize options, regardless of fetch result
      this.options = this.querySelectorAll(this.OPTION_SELECTOR);
      this.dispatchEvent(new CustomEvent('td:update-count', {
        bubbles: true,
        detail: { active: this.options }
      }));
      
      // Initialize any new elements
      this.options.forEach(option => {
        option.setAttribute('data-title', option.dataset.title || '');
        option.setAttribute('data-type', option.dataset.type || '');
      });
    }
  }


  customElements.define('td-list-collection', TDListCollection)
}

class TDDropdownContainer extends HTMLElement {
  constructor() {
    super()
    this.WRAPPER_SELECTOR = '.td--dropdown-wrapper'
    this.TRIGGER_SELECTOR = '.td--dropdown-trigger'

    this.trigger = undefined
    this.wrapper = undefined
    this.isOpen = false
  }
  connectedCallback() {
    setTimeout(() => {
      this.trigger = this.querySelector(this.TRIGGER_SELECTOR)
      this.wrapper = this.querySelector(this.WRAPPER_SELECTOR)
      if (this.trigger && this.wrapper) {
        this.bindDropdownEvents()
        this.initialize()
      } else {
        console.error('TD - Dropdown Container Missing Elements')
      }
      
    })
  }
  initialize() {
    // Stub
  }
  bindDropdownEvents() {
    this.trigger.addEventListener("click", this.handleDropdown.bind(this))
  }
  handleDropdown() {
    if (!this.isOpen) {
      this.openDropdown()
      document.addEventListener('click', this.maybeHandleClickOutside.bind(this))
    } else {
      this.closeDropdown()
      document.removeEventListener('click', this.maybeHandleClickOutside)
    }
    this.updateDropdownState()

  }
  maybeHandleClickOutside({target}) {
    if (!this.contains(target)) {
      this.closeDropdown()
      this.updateDropdownState()
      document.removeEventListener('click', this.maybeHandleClickOutside)
    }
  }
  openDropdown() {
    this.isOpen = true;
    this.wrapper.style.maxHeight = this.wrapper.scrollHeight + "px";
  }

  closeDropdown() {
    this.isOpen = false;
    this.wrapper.style.maxHeight = null;
  }

  updateDropdownState() {
    this.setAttribute('data-open', this.isOpen);
  }
}

if (!customElements.get('td-dropdown-sort-container')) {

  class TDSortContainer extends TDDropdownContainer {
    constructor() {
      super()

      this.OPTION_SELECTOR = '.td--sort-option__wrapper'
      this.TEXT_SELECTOR = '.td--sort-container__active-text'

      this.options = []
      this.selectedOption = ''
      this.textElement = undefined
      
      // Get state manager
      this.stateManager = getStateManager();
    }
    initialize() {
      this.textElement = this.querySelector(this.TEXT_SELECTOR)
      this.options = this.querySelectorAll(this.OPTION_SELECTOR)
      if (this.textElement && this.options.length) {
        this.bindEventListeners()
      }
      
      // Subscribe to state changes
      this.unsubscribe = this.stateManager.subscribe((newState) => {
        this.syncWithState(newState);
      });
    }
    
    syncWithState(state) {
      // Update UI based on state
      const { sortOption } = state;
      
      // Update sort options based on state
      this.options.forEach((option) => {
        if (option.dataset.value === sortOption) {
          option.setAttribute('data-active', true);
          this.textElement.innerHTML = option.dataset.text;
          this.selectedOption = sortOption;
        } else {
          option.setAttribute('data-active', false);
        }
      });
    }
    
    bindEventListeners() {
      this.options.forEach((elm) => elm.addEventListener('click', this.handleSortOptionSelected.bind(this)), true)
      
      // For backward compatibility
      document.addEventListener('td:update-sort-set', this.handleGlobalSortSet.bind(this))
      document.addEventListener('td:clear-all', this.handleGlobalClearSort.bind(this))
    }
    handleSortOptionSelected({target}) {
      let selectedValue = '';
      let selectedText = '';
      
      this.options.forEach((option) => {
        if (option == target || target.parentElement == option) {
          option.setAttribute('data-active', true);
          selectedText = option.dataset.text;
          selectedValue = option.dataset.value;
        } else {
          option.setAttribute('data-active', false);
        }
      });
      
      // Update state through state manager
      this.stateManager.setSortOption(selectedValue);
      
      // Update local state
      this.selectedOption = selectedValue;
      this.textElement.innerHTML = selectedText;
      
      // For backward compatibility
      this.dispatchEvent(new CustomEvent('td:sort-set', {
        bubbles: true,
        detail: { sort: selectedValue }
      }));
      
      this.handleDropdown();
    }
    handleGlobalSortSet({detail}) {
      // This is now handled by the state manager subscription
      // Just update UI for backward compatibility
      this.options.forEach((option) => {
        if (option.dataset.value == detail.value) {
          option.setAttribute('data-active', true);
          this.textElement.innerHTML = option.dataset.text;
          this.selectedOption = detail.value;
        } else {
          option.setAttribute('data-active', false);
        }
      });
    }
    
    handleGlobalClearSort() {
      // This is now handled by the state manager subscription
      // Just update UI for backward compatibility
      this.options.forEach((option) => {
        option.setAttribute('data-active', false);
      });
      
      if (this.textElement) {
        this.textElement.innerHTML = '';
      }
      
      // Clear all active text elements
      document.querySelectorAll('.td--sort-container__active-text, .td--sort-accordion__active-text').forEach(el => {
        el.innerHTML = '';
      });
      
      this.selectedOption = '';
    }
    
    disconnectedCallback() {
      // Unsubscribe from state changes
      if (this.unsubscribe) {
        this.unsubscribe();
      }
      
      // Clean up event listeners
      document.removeEventListener('td:update-sort-set', this.handleGlobalSortSet);
      document.removeEventListener('td:clear-all', this.handleGlobalClearSort);
    }
  }

  customElements.define('td-dropdown-sort-container', TDSortContainer)
}

if (!customElements.get('td-filter-container')) {

  class TDFilterContainer extends HTMLElement {
    constructor() {
      super()

      this.BUTTON_SELECTOR = '.td--collection-list__types-item'
      this.CLEAR_ALL_SELECTOR = '.td--compare-container__clear-all'
      this.ACTIVE_CLASS = '.active'

      this.filterSet = []
      
      // Get state manager
      this.stateManager = getStateManager();
    }

    connectedCallback() {
      document.addEventListener('DOMContentLoaded', () => {
        this.initialize()
      })
    }

    initialize() {
      this.filterButtons = this.querySelectorAll(this.BUTTON_SELECTOR)
      this.clearAll = this.querySelector(this.CLEAR_ALL_SELECTOR)

      if (this.clearAll && this.filterButtons.length) {
        this.bindEventListeners()
      }
      
      // Subscribe to state changes
      this.unsubscribe = this.stateManager.subscribe((newState) => {
        this.syncWithState(newState);
      });
    }
    
    syncWithState(state) {
      // Update UI based on state
      const { filterSet } = state;
      
      // Update filter buttons based on state
      this.filterButtons.forEach((button) => {
        const type = button.textContent.trim();
        if (filterSet.includes(type)) {
          button.classList.add('active');
        } else {
          button.classList.remove('active');
        }
      });
      
      // Update local state
      this.filterSet = [...filterSet];
    }

    bindEventListeners() {
      this.filterButtons.forEach((elm) => elm.addEventListener('click', this.handleFilterOptionSelected.bind(this)), true)
      this.clearAll.addEventListener('click', this.handleClearFilter.bind(this))
    }

    handleFilterOptionSelected({target}) {
      const type = target.textContent.trim();
      const index = this.filterSet.indexOf(type);
      let newFilterSet = [...this.filterSet];

      if (target.classList.contains('active')) {
        newFilterSet.splice(index, 1);
        target.classList.remove('active');
      } else {
        newFilterSet.push(type);
        target.classList.add('active');
      }
      
      // Update state through state manager
      this.stateManager.setFilterSet(newFilterSet);
      
      // Update local state
      this.filterSet = newFilterSet;
      
      // For backward compatibility
      this.dispatchEvent(new CustomEvent('td:filter-set', {
        bubbles: true, 
        detail: {type: newFilterSet}
      }));
    }
    
    handleClearFilter() {
      // Use state manager to clear all state
      this.stateManager.clearAll();
    }
    
    disconnectedCallback() {
      // Unsubscribe from state changes
      if (this.unsubscribe) {
        this.unsubscribe();
      }
    }
  }

  customElements.define('td-filter-container', TDFilterContainer)
}

class TDAccordionContainer extends HTMLElement {
  constructor() {
    super()
    this.CONTENT_SELECTOR = '.td--accordion-content'
    this.TRIGGER_SELECTOR = '.td--accordion-trigger'

    this.trigger = undefined
    this.content = undefined
    this.isOpen = false
  }
  connectedCallback() {
    setTimeout(() => {
      this.trigger = this.querySelector(this.TRIGGER_SELECTOR)
      this.content = this.querySelector(this.CONTENT_SELECTOR)
      if (this.trigger && this.content) {
        this.bindDropdownEvents()
        this.initialize()
      } else {
        console.error('TD - Accordion Container Missing Elements')
      }
      
    })
  }
  initialize() {
    // Stub
  }
  bindDropdownEvents() {
    this.trigger.addEventListener("click", this.handleDropdown.bind(this))
    document.addEventListener("td:acc-cont-opened", this.handleAccContOpened.bind(this))
    document.addEventListener('td:close-filter-accordions', () => {
      this.closeDropdown()
      this.updateDropdownState()
    })
  }
  handleDropdown() {
    if (!this.isOpen) {
      this.openDropdown()
    } else {
      this.closeDropdown()
    }
    this.updateDropdownState()
  }
  handleAccContOpened({detail}) {
    if (detail.elm !== this) {
      this.closeDropdown()
      this.updateDropdownState()
    }
  }
  openDropdown() {
    this.isOpen = true
    this.content.style.maxHeight = this.content.scrollHeight + "px";
    document.dispatchEvent(new CustomEvent('td:acc-cont-opened', {detail:{elm: this}}))
  }
  closeDropdown() {
    this.isOpen = false
    this.content.style.maxHeight = null
  }
  updateDropdownState() {
    this.setAttribute('data-open', this.isOpen)
  }
}
customElements.define('td-accordion-container', TDAccordionContainer)

if (!customElements.get('td-accordion-sort-container')) {

  class TDAccordionSortContainer extends TDAccordionContainer {
    constructor() {
      super()

      this.OPTION_SELECTOR = '.td--acc-sort-option__wrapper'
      this.TEXT_SELECTOR = '.td--sort-accordion__active-text'

      this.options = []
      this.selectedSortOptions = []
      this.selectedOption = ''
      this.textElement = undefined
      
      // Get state manager
      this.stateManager = getStateManager();
    }
    initialize() {
      this.textElement = this.querySelector(this.TEXT_SELECTOR)
      this.options = this.querySelectorAll(this.OPTION_SELECTOR)
      if (this.textElement && this.options.length) {
        this.bindEventListeners()
      }
      
      // Subscribe to state changes
      this.unsubscribe = this.stateManager.subscribe((newState) => {
        this.syncWithState(newState);
      });
    }
    
    syncWithState(state) {
      // Update UI based on state
      const { sortOption } = state;
      
      // Update sort options based on state
      this.options.forEach((option) => {
        if (option.dataset.value === sortOption) {
          option.setAttribute('data-active', true);
          this.textElement.innerHTML = option.dataset.text;
          this.selectedOption = sortOption;
        } else {
          option.setAttribute('data-active', false);
        }
      });
    }
    
    bindEventListeners() {
      this.options.forEach((elm) => elm.addEventListener('click', this.handleSortOptionSelected.bind(this)), true)
      
      // For backward compatibility
      document.addEventListener('td:update-sort-set', this.handleGlobalSortSet.bind(this))
      document.addEventListener('td:clear-all', this.handleGlobalClearSort.bind(this))
    }
    
    handleSortOptionSelected({target}) {
      let selectedValue = '';
      let selectedText = '';
      
      this.options.forEach((option) => {
        if (option == target || target.parentElement == option) {
          option.setAttribute('data-active', true);
          selectedText = option.dataset.text;
          selectedValue = option.dataset.value;
        } else {
          option.setAttribute('data-active', false);
        }
      });
      
      // Update state through state manager
      this.stateManager.setSortOption(selectedValue);
      
      // Update local state
      this.selectedOption = selectedValue;
      this.textElement.innerHTML = selectedText;
      
      // For backward compatibility
      this.dispatchEvent(new CustomEvent('td:sort-set', {
        bubbles: true,
        detail: {
          collections: this.selectedSortOptions,
          sort: selectedValue
        }
      }));
    }
    handleGlobalSortSet({detail}) {
      // This is now handled by the state manager subscription
      // Just update UI for backward compatibility
      this.options.forEach((option) => {
        if (option.dataset.value == detail.value) {
          option.setAttribute('data-active', true);
          this.textElement.innerHTML = option.dataset.text;
          this.selectedOption = detail.value;
        } else {
          option.setAttribute('data-active', false);
        }
      });
    }
    
    handleGlobalClearSort() {
      // This is now handled by the state manager subscription
      // Just update UI for backward compatibility
      this.options.forEach(option => option.setAttribute('data-active', false));
      this.selectedOption = '';

      // Clear text containers
      if (this.textElement) {
        this.textElement.innerHTML = '';
      }
    }
    
    disconnectedCallback() {
      // Unsubscribe from state changes
      if (this.unsubscribe) {
        this.unsubscribe();
      }
      
      // Clean up event listeners
      document.removeEventListener('td:update-sort-set', this.handleGlobalSortSet);
      document.removeEventListener('td:clear-all', this.handleGlobalClearSort);
    }
  }


  customElements.define('td-accordion-sort-container', TDAccordionSortContainer)
}

if (!customElements.get('td-accordion-filter-container')) {

  class TDAccordionFilterContainer extends TDAccordionContainer {
    constructor() {
      super()
      this.OPTION_SELECTOR = 'input'
      this.ACTIVE_ELM_SELECTOR = '.td--collection-accordion__active-text'

      this.activeCountElement = undefined
      this.options = []
      this.selectedOptions = []
      
      // Get state manager
      this.stateManager = getStateManager();
    }

    initialize() {
      this.options = this.querySelectorAll(this.OPTION_SELECTOR)
      this.activeCountElement = this.querySelector(this.ACTIVE_ELM_SELECTOR)

      if (this.options.length) {
        this.bindEventListeners()
      } else {
        console.error('TD - Compare Container Missing Elements')
      }
      
      // Subscribe to state changes
      this.unsubscribe = this.stateManager.subscribe((newState) => {
        this.syncWithState(newState);
      });
    }
    
    syncWithState(state) {
      // Update UI based on state
      const { filterSet } = state;
      
      // Update checkboxes based on state
      this.options.forEach((option) => {
        option.checked = filterSet.includes(option.value.trim());
      });
      
      // Update selected options
      this.updateSelectedOptions();
    }
    
    updateSelectedOptions() {
      this.selectedOptions = [];
      this.options.forEach((option) => {
        if (option.checked) {
          this.selectedOptions.push(option.value.trim());
        }
      });
      this.updateContainerUI();
    }

    bindEventListeners() {
      this.options.forEach((option) => option.addEventListener('click', this.handleFilterOptionSelected.bind(this)))
      
      // For backward compatibility
      document.addEventListener('td:clear-all', this.handleFiltersCleared.bind(this))
    }

    handleFilterOptionSelected({target}) {
      const type = target.value.trim();
      let newFilterSet = [...this.selectedOptions];
      const index = newFilterSet.indexOf(type);

      if (!target.checked) {
        newFilterSet.splice(index, 1);
      } else {
        newFilterSet.push(type);
      }
      
      // Update state through state manager
      this.stateManager.setFilterSet(newFilterSet);
      
      // For backward compatibility
      this.dispatchEvent(new CustomEvent('td:filter-set', {
        bubbles: true, 
        detail: {type: newFilterSet}
      }));
    }
    
    handleFiltersCleared() {
      // This is now handled by the state manager subscription
      // Just update UI for backward compatibility
      this.selectedOptions = [];
      this.options.forEach((option) => {
        option.checked = false;
      });
      this.updateContainerUI();
    }
    updateContainerUI() {
      if (this.selectedOptions.length) {
        if (this.activeCountElement) {
          this.activeCountElement.innerHTML = `(${this.selectedOptions.length})`;
        }
      } else {
        if (this.activeCountElement) {
          this.activeCountElement.innerHTML = null;
        }
      }
      
      // For backward compatibility
      this.dispatchEvent(new CustomEvent('td:update-slide-out-filters', {
        bubbles: true,
        detail: {type: this.selectedOptions}
      }));
    }
    
    disconnectedCallback() {
      // Unsubscribe from state changes
      if (this.unsubscribe) {
        this.unsubscribe();
      }
      
      // Clean up event listeners
      document.removeEventListener('td:clear-all', this.handleFiltersCleared);
    }
  }

  customElements.define('td-accordion-filter-container', TDAccordionFilterContainer)
}

if (!customElements.get('td-slideout-filters')) {

  class TDSlideoutFilters extends HTMLElement {
    constructor() {
      super()
      this.OVERLAY_SELECTOR = '.td--collection-list__slideout-overlay'
      this.CLOSER_SELECTOR = '.td--collection-list__slideout-closer'
      this.FILTER_TRIGGER_SELECTOR = '.td--collection-list__slideout-filter-trigger'
      this.FILTER_INDICATOR_SELECTOR = '.td--collection-list__slideout-filter-trigger-indicator'
      this.CLEAR_TRIGGER_SELECTOR = '.td--collection-list__slideout-clear-all'
      this.TEXT_SELECTOR = '.td--sort-accordion__active-text'
      this.CLOSE_SELECTOR = '.td--collection-list__slideout-close'
      this.FILTER_COUNT_SELECTOR = '.td--collection-list__slideout-filter-count'
      
      this.ACTIONS_SELECTOR = '.td--collection-list__slideout-footer'
      this.ACTIONS_SELECTOR_CLOSE = '.td--collection-list__slideout-footer--close'
      this.ACTIONS_MODIFIER_CLASS = 'td--collection-list__slideout-footer--hidden'
      this.COLLECTION_CARD_SELECTOR = '.td--acc-compare-option__wrapper'
      this.COLLECTION_CARDS_SELECTOR ='.td--collection-card__wrapper'
      
      this.overlay = undefined
      this.closer = undefined
      this.closeButton = undefined
      this.filterTrigger = undefined
      this.filterIndicator = undefined
      this.clearTrigger = undefined
      this.actionsContainer = undefined
      this.isOpen = false
      this.activeFilter = undefined
      this.allCollectionCards = undefined
      this.activeSort = 0

      this.activeCollections = []
      this.activeFilters = []
      
      // Get state manager
      this.stateManager = getStateManager();
    }
    connectedCallback() {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => {
          this.allCards = document.querySelectorAll(this.COLLECTION_CARDS_SELECTOR)
          this.overlay = this.querySelector(this.OVERLAY_SELECTOR)
          this.closer = this.querySelector(this.CLOSER_SELECTOR)
          this.filterTrigger = this.querySelector(this.FILTER_TRIGGER_SELECTOR)
          this.filterIndicator = document.querySelectorAll(this.FILTER_INDICATOR_SELECTOR)
          this.filterCount = document.querySelector(this.FILTER_COUNT_SELECTOR)
          this.clearTrigger = this.querySelector(this.CLEAR_TRIGGER_SELECTOR)
          this.actionsContainer = this.querySelector(this.ACTIONS_SELECTOR)
          this.actionsContainerClose = this.querySelector(this.ACTIONS_SELECTOR_CLOSE)
          this.closeButton = this.querySelector(this.CLOSE_SELECTOR)
          this.allCollectionCards = document.querySelectorAll(this.COLLECTION_CARD_SELECTOR)

          document.addEventListener("td:slideout-open", this.handleSlideoutOpen.bind(this))

          if (this.overlay) {
            this.overlay.addEventListener('click', this.handleSlideoutClose.bind(this))
          }
          
          // Subscribe to state changes
          this.unsubscribe = this.stateManager.subscribe((newState) => {
            this.syncWithState(newState);
          });
          
          // For backward compatibility
          if (this.actionsContainer) {
            document.addEventListener('td:update-slide-out-collections', this.updateSlideoutCollections.bind(this))
            document.addEventListener('td:update-slide-out-filters', this.updateSlideoutFilters.bind(this))
            document.addEventListener('td:sort-set', ({detail}) => {
              if (detail.sort != '') {
                this.activeSort = 1
              } else {
                this.activeSort = 0
              }
              this.updateSlideoutUI()
            })
          }
          if (this.closer) {
            this.closer.addEventListener('click', this.handleSlideoutClose.bind(this))
          }
          if (this.closeButton) {
            this.closeButton.addEventListener('click', this.handleSlideoutClose.bind(this))
          }
          if (this.filterTrigger) {
            this.filterTrigger.addEventListener('click', this.handleFilterTriggerClick.bind(this))
          }
          if (this.clearTrigger) {
            this.clearTrigger.addEventListener('click', this.handleClearTriggerClick.bind(this))
          }
          window.addEventListener('resize', this.handleWindowResize.bind(this))
        })
      })
    }
    
    syncWithState(state) {
      // Update UI based on state
      const { sortOption, comparisonSet, filterSet } = state;
      
      // Update local state
      this.activeCollections = [...comparisonSet];
      this.activeFilters = [...filterSet];
      this.activeSort = sortOption ? 1 : 0;
      
      // Update UI
      this.updateSlideoutUI();
    }
    disconnectedCallback() {
      // Unsubscribe from state changes
      if (this.unsubscribe) {
        this.unsubscribe();
      }
      
      window.removeEventListener('resize', this.handleWindowResize);
      
      // Clean up event listeners
      document.removeEventListener('td:update-slide-out-collections', this.updateSlideoutCollections);
      document.removeEventListener('td:update-slide-out-filters', this.updateSlideoutFilters);
      document.removeEventListener('td:slideout-open', this.handleSlideoutOpen);
    }
      handleWindowResize() {
        if (window.matchMedia("(min-width: 768px)").matches && this.isOpen) {
          this.handleSlideoutClose();
        }
      }
    updateSlideoutFilters({detail}) {
      this.activeFilters = detail.type
      this.updateSlideoutUI()
    }
    updateSlideoutCollections({detail}) {
      this.activeCollections = detail.collections
      this.updateSlideoutUI()
    }
    handleSlideoutOpen() {
      if (!this.isOpen) {
        this.isOpen = true
        this.updateUIState()
      }
    }
    handleSlideoutClose() {
      if (this.isOpen) {
        this.isOpen = false
        this.updateUIState()
        document.dispatchEvent(new CustomEvent('td:close-filter-accordions'))
      }
    }
    handleFilterTriggerClick() {
      // Update state through state manager
      this.stateManager.setComparisonSet(this.activeCollections);
      this.stateManager.setFilterSet(this.activeFilters);
      
      // For backward compatibility
      this.dispatchEvent(new CustomEvent('td:compare-set', {
        bubbles: true, 
        detail: {collections: this.activeCollections}
      }));
      this.dispatchEvent(new CustomEvent('td:filter-set', {
        bubbles: true, 
        detail: {type: this.activeFilters}
      }));
      
      this.handleSlideoutClose();
    }
    updateSlideoutUI() {
      this.activeFilter = document.querySelector(this.TEXT_SELECTOR) || '';
      const count = this.getCount();
      const hasActiveFilters = this.activeCollections.length > 0 || this.activeFilters.length > 0;
      const hasActiveSort = this.activeSort > 0;

      // Handle actions container visibility
      if (hasActiveFilters || hasActiveSort) {
        this.actionsContainerClose.classList.add(this.ACTIONS_MODIFIER_CLASS);
        this.actionsContainer.classList.remove(this.ACTIONS_MODIFIER_CLASS);
      } else {
        this.actionsContainer.classList.add(this.ACTIONS_MODIFIER_CLASS);
        this.actionsContainerClose.classList.remove(this.ACTIONS_MODIFIER_CLASS);
      }

      // Update filter count indicators
      const totalActiveCount = this.activeSort + this.activeFilters.length;
      if (this.filterCount) {
        this.filterCount.innerHTML = hasActiveFilters || hasActiveSort ? `(${totalActiveCount})` : '';
      }

      // Update filter indicators
      if (this.filterIndicator.length > 0) {
        this.filterIndicator.forEach(indicator => {
          indicator.innerHTML = hasActiveFilters || hasActiveSort ? `(${count})` : '';
        });
      }
    }
    getCount() {
      let count = 0
      if (this.allCards.length > 0) {
        if (this.activeCollections.length > 0) {
          this.allCards.forEach((card) => {
            if (this.activeCollections.includes(card.dataset.title)) {
              count++
            }
          })
        } else if (this.activeFilters.length > 0) {
          this.activeFilters.forEach((filter) => {
            let filterTotal = this.querySelector(`input[value="${filter}"]`).dataset.count
            if (filterTotal) {
              count += parseInt(filterTotal)
            }
          })
        } else {
          count = this.allCards.length
        }       
        return count
      }
    }
    handleClearTriggerClick() {
      // Use state manager to clear all state
      this.stateManager.clearAll();
      
      // Update UI
      if (this.filterCount) {
        this.filterCount.innerHTML = '';
      }
      if (this.filterIndicator.length > 0) {
        this.filterIndicator.forEach((indicator) => {
          indicator.textContent = '';
        });
      }
      this.handleSlideoutClose();
    }
    updateUIState() {
      this.setAttribute('data-open', this.isOpen)
      document.firstElementChild.classList.toggle('overflow-hidden', this.isOpen)
    }
  }

  customElements.define('td-slideout-filters', TDSlideoutFilters)
}
